// PROTOTYPE
import * as React from 'react';
import { $getSelection, $isRangeSelection, $setSelection } from 'lexical';
import type { InputRef } from 'antd';
import type { LexicalEditor, RangeSelection } from 'lexical';
import {
  parseVideoEmbedUrl,
  validateVideoFile,
  type VideoUploadError,
} from '../../videoEmbed';
import type { VideoPayload } from '../../nodes/VideoNode';
import { INSERT_VIDEO_COMMAND } from '../VideosPlugin';
import type { ToolbarVideoController, VideoTabKey } from './types';

export function useToolbarVideo(
  editor: LexicalEditor
): ToolbarVideoController {
  const [activeTab, setActiveTab] = React.useState<VideoTabKey>('embed');
  const [embedError, setEmbedError] = React.useState<'' | 'invalid'>('');
  const [embedUrlValue, setEmbedUrlValue] = React.useState('');
  const [isEditorVisible, setIsEditorVisible] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<'' | VideoUploadError>(
    ''
  );
  const savedSelectionRef = React.useRef<RangeSelection | null>(null);
  const inputRef = React.useRef<InputRef>(null);
  const fileInputRef = React.useRef<React.ComponentRef<'input'>>(null);

  // Clears a stale "invalid URL" error as soon as the author edits the field again.
  React.useEffect(() => {
    setEmbedError('');
  }, [embedUrlValue]);

  const closeEditor = React.useCallback(() => {
    setIsEditorVisible(false);
    setActiveTab('embed');
    setEmbedError('');
    setEmbedUrlValue('');
    setUploadError('');
    savedSelectionRef.current = null;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const openEditor = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        savedSelectionRef.current = selection.clone();
      } else {
        savedSelectionRef.current = null;
      }
    });

    setIsEditorVisible(true);
  }, [editor]);

  const insertVideo = React.useCallback(
    (payload: VideoPayload) => {
      editor.update(() => {
        if (savedSelectionRef.current) {
          $setSelection(savedSelectionRef.current);
        }
      });

      editor.dispatchCommand(INSERT_VIDEO_COMMAND, payload);
      closeEditor();
    },
    [closeEditor, editor]
  );

  const submitEmbed = React.useCallback(() => {
    const embed = parseVideoEmbedUrl(embedUrlValue);

    if (!embed) {
      setEmbedError('invalid');
      return;
    }

    insertVideo({
      provider: embed.provider,
      source: 'embed',
      src: embed.embedUrl,
    });
  }, [embedUrlValue, insertVideo]);

  const handleFileChange = React.useCallback<
    NonNullable<React.ComponentPropsWithoutRef<'input'>['onChange']>
  >(
    (event) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      const error = validateVideoFile(file);

      if (error) {
        setUploadError(error);
        event.target.value = '';
        return;
      }

      // Intentionally never revoked: revoking it would blank the node rendering it.
      insertVideo({
        source: 'upload',
        src: globalThis.URL.createObjectURL(file),
      });
    },
    [insertVideo]
  );

  return {
    activeTab,
    closeEditor,
    embedError,
    embedUrlValue,
    fileInputRef,
    handleFileChange,
    inputRef,
    isEditorVisible,
    openEditor,
    setActiveTab,
    setEmbedUrlValue,
    submitEmbed,
    uploadError,
  };
}
